import { I18nProvider } from '@contexts/I18nContext'
import { NotificationProvider } from '@contexts/NotificationContext'
import { PortalRolesProvider } from '@contexts/PortalRolesContext'
import { Global } from '@emotion/react'
import { KeycloakProvider, useKeycloak } from '@lib/keycloak'
import { isolateMobxGlobalState } from '@lib/mobx'
import { QueryProvider } from '@lib/react-query'
import { initSentry } from '@lib/sentry'
import * as Sentry from '@sentry/react'
import { RootErrorFallback } from '@shared/components/ErrorFallback'
import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'

import { SuiProvider } from '@hrworks/sui-core/SuiProvider'

import { setKeycloakInstance } from './api/client'
import { setSessionExpiredHandler } from './lib/session/sessionExpired'
import { router } from './routes/AppRoutes'
import { SessionExpiredModal } from './shared/components/SessionExpiredModal/SessionExpiredModal'
import { globalStyles, theme } from './styles'

// Import SmartFace critical styles (CSS variables, fonts, base styles).
// Local copy — see the note in src/styles/critical.css for why the vendored
// file can't be imported directly.
import './styles/critical.css'

// Initialize Sentry before rendering the app. isolateMobxGlobalState() must run
// after this so its foreign-MobX warning is deliverable, and before render so
// MobX has no live reactions (isolateGlobalState() throws otherwise).
initSentry()
isolateMobxGlobalState()

const KeycloakConnector = ({ children }: { children: React.ReactNode }) => {
  const { keycloak } = useKeycloak()

  useEffect(() => {
    if (keycloak) {
      setKeycloakInstance(keycloak)
    }
  }, [keycloak])

  return <>{children}</>
}

const NO_ROLES: readonly string[] = []

// Hands the support user's roles for this portal's own Keycloak client to the app.
const PortalRolesBridge = ({ children }: { children: React.ReactNode }) => {
  const { keycloak } = useKeycloak()
  const clientId = keycloak?.clientId
  const roles = (clientId && keycloak.resourceAccess?.[clientId]?.roles) || NO_ROLES
  return <PortalRolesProvider roles={roles}>{children}</PortalRolesProvider>
}

// Wires the session-expired handler on mount so client.ts and KeycloakProvider
// can trigger the modal without a direct React dependency.
const SessionExpiredBridge = ({ children }: { children: React.ReactNode }) => {
  const [sessionExpired, setSessionExpired] = useState(false)
  const { keycloak } = useKeycloak()

  useEffect(() => {
    setSessionExpiredHandler(() => setSessionExpired(true))
    return () => setSessionExpiredHandler(null)
  }, [])

  return (
    <>
      {children}
      {sessionExpired && <SessionExpiredModal onConfirm={() => void keycloak?.login()} />}
    </>
  )
}

const root = createRoot(document.getElementById('root') as HTMLElement)

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={RootErrorFallback} showDialog={false}>
      <KeycloakProvider>
        <SuiProvider customTheme={theme} useCustomScrollbars={true} iconSet="streamline" theme="light">
          <Global styles={globalStyles(theme)} />
          <I18nProvider>
            <NotificationProvider>
              <QueryProvider>
                <KeycloakConnector>
                  <PortalRolesBridge>
                    <SessionExpiredBridge>
                      <RouterProvider router={router} />
                    </SessionExpiredBridge>
                  </PortalRolesBridge>
                </KeycloakConnector>
              </QueryProvider>
            </NotificationProvider>
          </I18nProvider>
        </SuiProvider>
      </KeycloakProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
)
